import { z } from "zod";

/**
 * A single generated prompt returned inside discovery session
 * responses. Also reused (extended with the workflow-written extras)
 * by the webapp-only GET /api/visibility/query-discovery/active
 * contract in ./webapp/visibility.
 */
export const DiscoveryPrompt = z.object({
  prompt: z.string(),
  category: z.string(),
  geoQualified: z.boolean().optional(),
  containsCompetitor: z.boolean().optional(),
  containsBrand: z.boolean().optional(),
});
export type DiscoveryPrompt = z.infer<typeof DiscoveryPrompt>;

/**
 * Wire-level response for POST /api/visibility/query-discovery/run.
 * The route returns this on success after starting the Temporal
 * queryDiscoveryWorkflow.
 */
export const QueryDiscoveryRunResponse = z.object({
  sessionId: z.string(),
  workflowId: z.string(),
  status: z.literal("generating"),
});
export type QueryDiscoveryRunResponse = z.infer<
  typeof QueryDiscoveryRunResponse
>;

/**
 * Wire-level response for
 * GET /api/visibility/query-discovery/status/:sessionId.
 *
 * A flat schema covers all branches; `prompts`, `summary`,
 * `progress`, `completedAt`, and `error` are optional and appear
 * only in their respective status branches.
 *
 * status values:
 *   "generating" — workflow running; `progress` may be present.
 *   "review"     — prompts ready for user review; `prompts` +
 *                  `summary.totalGenerated` present.
 *   "accepted"   — user accepted; `summary` with full counts
 *                  present, `completedAt` present.
 *   "expired"    — session expired or workflow failed; `error`
 *                  explains the reason.
 */
export const QueryDiscoveryStatusResponse = z.object({
  status: z.string(),
  sessionId: z.string().optional(),
  prompts: z.array(DiscoveryPrompt).optional(),
  summary: z
    .object({
      totalGenerated: z.number().optional(),
      totalAccepted: z.number().optional(),
      totalRejected: z.number().optional(),
      totalEdited: z.number().optional(),
      totalFinal: z.number().optional(),
    })
    .optional(),
  progress: z
    .object({
      step: z.string().optional(),
      pct: z.number().optional(),
    })
    .optional(),
  completedAt: z.string().optional(),
  createdAt: z.string().optional(),
  temporalUnavailable: z.boolean().optional(),
  error: z.string().optional(),
});
export type QueryDiscoveryStatusResponse = z.infer<
  typeof QueryDiscoveryStatusResponse
>;

/**
 * Wire-level response for POST /api/visibility/query-discovery/accept.
 * The route returns an acceptance summary. The CLI discards this
 * value but we assert the shape so a server-side contract change
 * fails fast at the boundary.
 */
export const QueryDiscoveryAcceptResponse = z.object({
  success: z.boolean(),
  summary: z.object({
    accepted: z.number(),
    rejected: z.number(),
    edited: z.number(),
    totalCreated: z.number(),
  }),
  promptIds: z.array(z.string()),
});
export type QueryDiscoveryAcceptResponse = z.infer<
  typeof QueryDiscoveryAcceptResponse
>;

// ── Route 11: GET /api/visibility/brand-prompts ──────────────────

const PromptDimensions = z.object({
  purchaseIntent: z.number(),
  thoughtLeadership: z.number(),
  brandAwareness: z.number(),
  competitiveDefense: z.number(),
  brandRelevance: z.number(),
  modelUsed: z.string(),
  scoredAt: z.string(),
});

const LegacyValueScore = z.object({
  totalScore: z.number(),
  label: z.string(),
  signalScore: z.number(),
  differentiationScore: z.number(),
  trendScore: z.number(),
  strategicAlignmentScore: z.number(),
  uniquenessScore: z.number(),
});

/**
 * A single brand prompt row as returned by
 * GET /api/visibility/brand-prompts.
 */
export const BrandPrompt = z.object({
  id: z.string(),
  projectId: z.string(),
  prompt: z.string(),
  category: z.string(),
  source: z.string(),
  isActive: z.boolean(),
  status: z.string(),
  deactivationReason: z.string().nullable(),
  consecutiveLowScans: z.number(),
  tags: z.array(z.string()),
  dimensions: PromptDimensions.nullable(),
  valueScore: LegacyValueScore.nullable(),
  createdAt: z.string().nullable(),
  updatedAt: z.string().nullable(),
});
export type BrandPrompt = z.infer<typeof BrandPrompt>;

/**
 * Wire-level response for GET /api/visibility/brand-prompts.
 */
export const BrandPromptsResponse = z.object({
  prompts: z.array(BrandPrompt),
});
export type BrandPromptsResponse = z.infer<typeof BrandPromptsResponse>;

// ── Route 12: GET /api/visibility/brand-config ───────────────────

/**
 * Strategy-dimension weights stored in
 * brand_config.strategy_weights. Exported so
 * webapp/visibility.ts (POST /api/visibility/brand-config) can
 * reuse the same schema without duplication.
 */
export const StrategyWeights = z.object({
  purchase_intent: z.number(),
  thought_leadership: z.number(),
  brand_awareness: z.number(),
  competitive_defense: z.number(),
});
export type StrategyWeights = z.infer<typeof StrategyWeights>;

/**
 * Wire-level response for GET /api/visibility/brand-config.
 * Only `strategyWeights` is consumed by the CLI; the full shape —
 * including the extended brand-profile fields the webapp settings
 * page renders — is captured so every caller can rely on it.
 * (An earlier draft omitted the extended fields, which made
 * respondWith strip them from the wire and silently blanked the
 * settings page's profile section — keep this in sync with the
 * route's full payload.)
 */
export const BrandConfigResponse = z.object({
  id: z.string().nullable().optional(),
  projectId: z.string().optional(),
  brandNames: z.array(z.string()).optional(),
  industry: z.string().nullable().optional(),
  keywords: z.array(z.string()).optional(),
  competitors: z
    .array(
      z.object({
        id: z.string(),
        names: z.array(z.string()),
        tags: z.array(z.string()),
      }),
    )
    .optional(),
  isActive: z.boolean().optional(),
  createdAt: z.string().nullable().optional(),
  updatedAt: z.string().nullable().optional(),
  // Extended profile fields (webapp settings page)
  brandUrl: z.string().optional(),
  products: z.array(z.string()).optional(),
  features: z.array(z.string()).optional(),
  useCases: z.array(z.string()).optional(),
  personas: z.array(z.string()).optional(),
  targetLocations: z.array(z.string()).optional(),
  integrationsOrBrandsCarried: z.array(z.string()).optional(),
  targetPersona: z.string().optional(),
  brandNameVariations: z.array(z.string()).optional(),
  businessType: z.string().optional(),
  pricingModel: z.string().optional(),
  signals: z.record(z.unknown()).optional(),
  source: z.string().optional(),
  extractionStatus: z.string().optional(),
  workflowId: z.string().optional(),
  strategyProfile: z.string().optional(),
  strategyWeights: StrategyWeights.optional(),
});
export type BrandConfigResponse = z.infer<typeof BrandConfigResponse>;

// ── Route 13: GET /api/visibility/brand-check/history ───────────

/**
 * Per-engine outcome of the scan that produced this history. Mirrors
 * temporal/src/shared/engine-outcomes.ts, which the workflow writes to
 * workflow_metadata.engine_outcomes.
 */
export const EngineOutcome = z.object({
  status: z.enum(["ok", "partial", "failed", "skipped"]),
  reason: z
    .enum(["missing_credentials", "quota_exhausted", "provider_error"])
    .optional(),
  detail: z.string().optional(),
  checksPlanned: z.number(),
  checksSucceeded: z.number(),
  checksFailed: z.number(),
});
export type EngineOutcome = z.infer<typeof EngineOutcome>;

export const EngineOutcomes = z.record(EngineOutcome);
export type EngineOutcomes = z.infer<typeof EngineOutcomes>;

const EngineCheckRow = z.object({
  engine: z.string(),
  mentionRate: z.number(),
  mentioned: z.boolean().optional(),
  totalRuns: z.number().optional(),
  mentions: z.number().optional(),
  latestCheckedAt: z.string().nullable().optional(),
  sampleResponseText: z.string().nullable().optional(),
  sources: z.array(z.string()).nullable().optional(),
  prevMentionRate: z.number().nullable().optional(),
  avgPosition: z.number().nullable().optional(),
  // Quality of mention: avg sentiment (0–100) of the self brand over
  // self-mentioned runs for this engine; null when not mentioned.
  avgSentiment: z.number().nullable().optional(),
});

const CheckHistoryPrompt = z.object({
  promptId: z.string(),
  promptText: z.string().optional(),
  category: z.string().nullable().optional(),
  mentionedEntities: z.array(z.string()).nullable().optional(),
  avgMentionValue: z.number().nullable().optional(),
  latestCheckedAt: z.string().nullable().optional(),
  allCompetitorsMentioned: z.array(z.string()).nullable().optional(),
  engines: z.array(EngineCheckRow),
  competitorRates: z.record(z.number()),
});

/**
 * Wire-level response for GET /api/visibility/brand-check/history.
 * Mirrors the full get_brand_check_history RPC output
 * (supabase/schemas/brand-visibility.sql) — the route spreads the
 * RPC result straight into respondWith, so any field missing here
 * is stripped from the wire. An earlier draft captured only the
 * CLI-consumed subset, which silently blanked the webapp's
 * BrandPromptsTable fields. Keep in sync with the RPC.
 */
export const CheckHistoryResponse = z.object({
  prompts: z.array(CheckHistoryPrompt).optional(),
  total: z.number().optional(),
  limit: z.number().optional(),
  offset: z.number().optional(),
  allCompetitors: z.array(z.string()).optional(),
  competitorTypes: z.record(z.string()).nullable().optional(),
  workflowId: z.string().nullable().optional(),
  previousWorkflowId: z.string().nullable().optional(),
  engineOutcomes: EngineOutcomes.nullable().optional(),
});
export type CheckHistoryResponse = z.infer<typeof CheckHistoryResponse>;
